// keyrd_console/src/components/DevLogsViewer.js
import React, { useState } from "react";
import { buildKeyrdHeaders } from "../api/headers";

function DevLogsViewer({ auth }) {
  const { baseUrl, token, authorized, registryVersion, registryHash } = auth || {};

  const [path, setPath] = useState("/api/logs/history");
  const [patientId, setPatientId] = useState("");
  const [limit, setLimit] = useState("50");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [response, setResponse] = useState(null);

  const runQuery = () => {
    if (!authorized || !baseUrl) return;

    setLoading(true);
    setErr(null);
    setResponse(null);

    // Build query string from optional fields
    const params = new URLSearchParams();
    if (patientId.trim()) params.append("patient_id", patientId.trim());
    if (limit.trim()) params.append("limit", limit.trim());

    const qs = params.toString();
    const url = `${baseUrl}${path}${qs ? `?${qs}` : ""}`;

    fetch(url, {
      headers: buildKeyrdHeaders({ token, registryVersion, registryHash }),
    })
      .then(async (res) => {
        const text = await res.text();
        let payload;
        try {
          payload = text ? JSON.parse(text) : null;
        } catch {
          payload = { raw: text };
        }

        if (!res.ok) {
          const msg = payload && payload.error ? payload.error : `HTTP ${res.status}`;
          throw new Error(msg);
        }

        setResponse(payload);
      })
      .catch((e) => setErr(e.message || String(e)))
      .finally(() => setLoading(false));
  };

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
        Developer Logs
      </h1>
      <p style={{ fontSize: 13, color: "#4b5563", marginBottom: 16 }}>
        Lightweight query console for KeyRD log endpoints. Defaults to{" "}
        <code>/api/logs/history</code> but you can point it at any path
        (e.g. <code>/admin/metrics</code>, <code>/console/metrics</code>, or
        future log routes).
      </p>

      {!authorized && (
        <div
          style={{
            padding: 10,
            borderRadius: 8,
            border: "1px solid #f97316",
            backgroundColor: "#fffbeb",
            fontSize: 13,
          }}
        >
          Paste a bearer token in the top bar to use the developer logs viewer.
        </div>
      )}

      {authorized && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 10,
            backgroundColor: "#ffffff",
            boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
            border: "1px solid #e5e7eb",
          }}
        >
          <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 600 }}>
            Request
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <div style={{ gridColumn: "1 / -1" }}>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  marginBottom: 2,
                  color: "#374151",
                }}
              >
                Endpoint path
              </label>
              <input
                type="text"
                value={path}
                onChange={(e) => setPath(e.target.value)}
                placeholder="/api/logs/history"
                style={{
                  width: "100%",
                  padding: 6,
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  fontSize: 12,
                  fontFamily: "ui-monospace, SFMono-Regular, monospace",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  marginBottom: 2,
                  color: "#374151",
                }}
              >
                patient_id (optional)
              </label>
              <input
                type="text"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                placeholder="2222-...."
                style={{
                  width: "100%",
                  padding: 6,
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  fontSize: 12,
                  fontFamily: "ui-monospace, SFMono-Regular, monospace",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  marginBottom: 2,
                  color: "#374151",
                }}
              >
                limit (optional)
              </label>
              <input
                type="number"
                min="1"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                style={{
                  width: "100%",
                  padding: 6,
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  fontSize: 12,
                }}
              />
            </div>
          </div>

          <button
            onClick={runQuery}
            disabled={loading}
            style={{
              padding: "6px 12px",
              borderRadius: 999,
              border: "none",
              backgroundColor: "#0f766e",
              color: "#ecfdf5",
              fontSize: 12,
              fontWeight: 600,
              cursor: loading ? "default" : "pointer",
            }}
          >
            {loading ? "Runningâ€¦" : "Run query"}
          </button>
        </div>
      )}

      {authorized && err && (
        <div
          style={{
            marginBottom: 12,
            padding: 8,
            borderRadius: 6,
            border: "1px solid #fecaca",
            backgroundColor: "#fef2f2",
            fontSize: 12,
            color: "#b91c1c",
          }}
        >
          Error: {err}
        </div>
      )}

      {authorized && response && (
        <div
          style={{
            padding: 12,
            borderRadius: 10,
            backgroundColor: "#0f172a",
            color: "#e5e7eb",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            fontSize: 12,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {JSON.stringify(response, null, 2)}
        </div>
      )}

      {authorized && !loading && !err && !response && (
        <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>
          No response yet. Configure an endpoint and run a query.
        </div>
      )}
    </div>
  );
}

export default DevLogsViewer;
